// @ts-nocheck
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { shouldSkipNativeSystemBarManipulation } from '@/utils/androidPlatform';
import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';

export const isNativePlatform = () => Capacitor.isNativePlatform();

/**
 * V213.1: Initialize Capacitor app
 * Note: Back button handling is now done via useAndroidBackButton hook
 * which provides priority-based modal handling and uses minimizeApp instead of exitApp
 */
export const initializeApp = async () => {
  if (!isNativePlatform()) return;

  try {
    // Hide splash screen
    await SplashScreen.hide();

    // V279.1: Style status bar - match app theme (V335.4: theme-aware via syncStatusBarToTheme)
    const isDark =
      typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
    await syncStatusBarToTheme(isDark);
    
    // V213.1: Back button is now handled by useAndroidBackButton hook
    // which uses minimizeApp on root instead of exitApp
    // See: src/hooks/useAndroidBackButton.ts

    // V291-FIX: Remove keyboard-open class and --keyboard-height CSS var for Android native
    // Android native uses adjustResize + KeyboardResize.Native, which handles layout automatically
    // These legacy hacks conflict with native resize behavior
    // Only keep for iOS web if needed, but for Capacitor Android native: NO body class, NO css vars
    const platform = Capacitor.getPlatform();
    if (platform !== 'android') {
      // iOS web fallback (if needed) - but not for Capacitor native
      if (!Capacitor.isNativePlatform()) {
        Keyboard.addListener('keyboardWillShow', (info) => {
          document.body.classList.add('keyboard-open');
          document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
        });

        Keyboard.addListener('keyboardWillHide', () => {
          document.body.classList.remove('keyboard-open');
          document.body.style.removeProperty('--keyboard-height');
        });
      }
    }

    // Handle app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active?', isActive);
    });

    console.log('✅ Capacitor initialized successfully');
  } catch (error) {
    console.error('❌ Error initializing Capacitor:', error);
  }
};

export const getPlatformInfo = () => {
  return {
    platform: Capacitor.getPlatform(),
    isNative: Capacitor.isNativePlatform(),
    isAndroid: Capacitor.getPlatform() === 'android',
    isIOS: Capacitor.getPlatform() === 'ios',
    isWeb: Capacitor.getPlatform() === 'web'
  };
};

export const setStatusBarStyle = async (style: 'light' | 'dark') => {
  if (!isNativePlatform()) return;
  
  try {
    await StatusBar.setStyle({ 
      style: style === 'light' ? Style.Light : Style.Dark 
    });
  } catch (error) {
    console.error('Error setting status bar style:', error);
  }
};

/** V335.4 / V601: Sync StatusBar background + style to theme. Skips deprecated
 *  Window.setStatusBarColor bridge calls on every Android version (UA-based
 *  Android 15 detection is unsafe; see androidPlatform.ts). */
export const syncStatusBarToTheme = async (isDark: boolean) => {
  if (!isNativePlatform()) return;
  try {
    if (!shouldSkipNativeSystemBarManipulation()) {
      const color = isDark ? '#1a1a2e' : '#FFFFFF';
      await StatusBar.setBackgroundColor({ color });
    }
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
  } catch (error) {
    console.error('Error syncing status bar to theme:', error);
  }
};

/** V335.4: Update theme-color meta tag for Android status bar / PWA. */
export const setThemeColorMeta = (isDark: boolean) => {
  const color = isDark ? '#1a1a2e' : '#FFFFFF';
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', color);
};

export const hideKeyboard = async () => {
  if (!isNativePlatform()) return;
  
  try {
    await Keyboard.hide();
  } catch (error) {
    console.error('Error hiding keyboard:', error);
  }
};

